import type { RangeKey } from '@dataTypes/dashboard.types'

export type ChairTimeKpiData = {
  totalChairTime: string
  completedAppts: string
  scheduledTime: string
  openChairTime: string
  efficiencyRate: string
  cancellationRate: string
  comparisonLabel: string
  efficiencyDelta: string
  efficiencyTrend: 'up' | 'down'
  scheduledDelta: string
  scheduledTrend: 'up' | 'down'
  openDelta: string
  openTrend: 'up' | 'down'
  cancellationDelta: string
  cancellationTrend: 'up' | 'down'
}

const CHAIR_TIME_KPI: Record<RangeKey, ChairTimeKpiData> = {
  '1D': {
    totalChairTime: '40 hrs',
    completedAppts: '95%',
    scheduledTime: '34%',
    openChairTime: '6 hrs',
    efficiencyRate: '89%',
    cancellationRate: '3.2%',
    comparisonLabel: 'vs. yesterday',
    efficiencyDelta: '6% (3.4 hrs)',
    efficiencyTrend: 'up',
    scheduledDelta: '6% (1 hr)',
    scheduledTrend: 'up',
    openDelta: '6% (1 hr)',
    openTrend: 'down',
    cancellationDelta: '12%',
    cancellationTrend: 'down',
  },
  '1W': {
    totalChairTime: '200 hrs',
    completedAppts: '92%',
    scheduledTime: '86%',
    openChairTime: '28 hrs',
    efficiencyRate: '86%',
    cancellationRate: '4.5%',
    comparisonLabel: 'vs. last week',
    efficiencyDelta: '3% (5.5 hrs)',
    efficiencyTrend: 'up',
    scheduledDelta: '5% (8.2 hrs)',
    scheduledTrend: 'up',
    openDelta: '12% (3.8 hrs)',
    openTrend: 'down',
    cancellationDelta: '1.1%',
    cancellationTrend: 'down',
  },
  '1M': {
    totalChairTime: '840 hrs',
    completedAppts: '91%',
    scheduledTime: '85%',
    openChairTime: '126 hrs',
    efficiencyRate: '85%',
    cancellationRate: '5.1%',
    comparisonLabel: 'vs. last month',
    efficiencyDelta: '2% (15.1 hrs)',
    efficiencyTrend: 'up',
    scheduledDelta: '4% (27.4 hrs)',
    scheduledTrend: 'up',
    openDelta: '8% (9.4 hrs)',
    openTrend: 'down',
    cancellationDelta: '0.6%',
    cancellationTrend: 'down',
  },
  '1Y': {
    totalChairTime: '10,080 hrs',
    completedAppts: '90%',
    scheduledTime: '85%',
    openChairTime: '1,512 hrs',
    efficiencyRate: '85%',
    cancellationRate: '5.8%',
    comparisonLabel: 'vs. last year',
    efficiencyDelta: '3% (242 hrs)',
    efficiencyTrend: 'up',
    scheduledDelta: '5% (408 hrs)',
    scheduledTrend: 'up',
    openDelta: '10% (168 hrs)',
    openTrend: 'down',
    cancellationDelta: '0.9%',
    cancellationTrend: 'down',
  },
  YTD: {
    totalChairTime: '2,520 hrs',
    completedAppts: '90%',
    scheduledTime: '85%',
    openChairTime: '378 hrs',
    efficiencyRate: '85%',
    cancellationRate: '5.4%',
    comparisonLabel: 'vs. YTD last year',
    efficiencyDelta: '3% (61 hrs)',
    efficiencyTrend: 'up',
    scheduledDelta: '4% (82 hrs)',
    scheduledTrend: 'up',
    openDelta: '9% (37 hrs)',
    openTrend: 'down',
    cancellationDelta: '0.7%',
    cancellationTrend: 'down',
  },
}

export function getChairTimeKpi(range: RangeKey): ChairTimeKpiData {
  return CHAIR_TIME_KPI[range]
}

export type ChairTimeDonutData = {
  scheduled: number
  available: number
  efficiencyPct: number
  goalPct: number
}

const CHAIR_TIME_DONUT: Record<RangeKey, ChairTimeDonutData> = {
  '1D': { scheduled: 34, available: 40, efficiencyPct: 89, goalPct: 75 },
  '1W': { scheduled: 172, available: 200, efficiencyPct: 86, goalPct: 75 },
  '1M': { scheduled: 714, available: 840, efficiencyPct: 85, goalPct: 75 },
  '1Y': { scheduled: 8568, available: 10080, efficiencyPct: 85, goalPct: 75 },
  YTD: { scheduled: 2142, available: 2520, efficiencyPct: 85, goalPct: 75 },
}

export function getChairTimeDonut(range: RangeKey): ChairTimeDonutData {
  return CHAIR_TIME_DONUT[range]
}

export type ChairTimeChartPoint = {
  label: string
  scheduled: number
  open: number
}

export type ChairTimeChartConfig = {
  data: ChairTimeChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
  axisUnit: 'minutes' | 'hours'
}

const CHAIR_TIME_CHART: Record<RangeKey, ChairTimeChartConfig> = {
  '1D': {
    data: [
      { label: '8 AM', scheduled: 31, open: 8 },
      { label: '9 AM', scheduled: 39, open: 9 },
      { label: '10 AM', scheduled: 29, open: 7 },
      { label: '11 AM', scheduled: 35, open: 7 },
      { label: '12 PM', scheduled: 21, open: 8 },
      { label: '1 PM', scheduled: 12, open: 8 },
      { label: '2 PM', scheduled: 21, open: 10 },
      { label: '3 PM', scheduled: 21, open: 8 },
      { label: '4 PM', scheduled: 21, open: 8 },
      { label: '5 PM', scheduled: 38, open: 7 },
    ],
    yAxisDomain: [0, 60],
    yAxisTicks: [0, 20, 40, 60],
    axisUnit: 'minutes',
  },
  '1W': {
    data: [
      { label: 'Mon', scheduled: 36, open: 4 },
      { label: 'Tue', scheduled: 38, open: 2 },
      { label: 'Wed', scheduled: 34, open: 6 },
      { label: 'Thu', scheduled: 36, open: 4 },
      { label: 'Fri', scheduled: 28, open: 12 },
    ],
    yAxisDomain: [0, 50],
    yAxisTicks: [0, 10, 20, 30, 40, 50],
    axisUnit: 'hours',
  },
  '1M': {
    data: [
      { label: 'Wk 1', scheduled: 182, open: 28 },
      { label: 'Wk 2', scheduled: 190, open: 20 },
      { label: 'Wk 3', scheduled: 176, open: 34 },
      { label: 'Wk 4', scheduled: 166, open: 44 },
    ],
    yAxisDomain: [0, 250],
    yAxisTicks: [0, 50, 100, 150, 200, 250],
    axisUnit: 'hours',
  },
  '1Y': {
    data: [
      { label: 'Apr', scheduled: 688, open: 152 },
      { label: 'May', scheduled: 712, open: 128 },
      { label: 'Jun', scheduled: 700, open: 140 },
      { label: 'Jul', scheduled: 726, open: 114 },
      { label: 'Aug', scheduled: 718, open: 122 },
      { label: 'Sep', scheduled: 694, open: 146 },
      { label: 'Oct', scheduled: 708, open: 132 },
      { label: 'Nov', scheduled: 720, open: 120 },
      { label: 'Dec', scheduled: 676, open: 164 },
      { label: 'Jan', scheduled: 700, open: 140 },
      { label: 'Feb', scheduled: 716, open: 124 },
      { label: 'Mar', scheduled: 730, open: 110 },
    ],
    yAxisDomain: [0, 900],
    yAxisTicks: [0, 300, 600, 900],
    axisUnit: 'hours',
  },
  YTD: {
    data: [
      { label: 'Jan', scheduled: 700, open: 140 },
      { label: 'Feb', scheduled: 716, open: 124 },
      { label: 'Mar', scheduled: 726, open: 114 },
    ],
    yAxisDomain: [0, 900],
    yAxisTicks: [0, 300, 600, 900],
    axisUnit: 'hours',
  },
}

export function getChairTimeChartConfig(range: RangeKey): ChairTimeChartConfig {
  return CHAIR_TIME_CHART[range]
}

/* ─── Chair time by provider ──────────────────────────────────────────────── */

export type ChairTimeByProviderRow = {
  id: string
  provider: string
  role: 'Dentist' | 'Hygienist'
  totalHrs: string
  scheduledHrs: string
  openHrs: string
  efficiency: string
  completedAppts: number
  noShows: number
}

const BY_PROVIDER: Record<RangeKey, ChairTimeByProviderRow[]> = {
  '1D': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      role: 'Dentist',
      totalHrs: '8 hrs',
      scheduledHrs: '7.5 hrs',
      openHrs: '0.5 hrs',
      efficiency: '94%',
      completedAppts: 9,
      noShows: 1,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      role: 'Dentist',
      totalHrs: '8 hrs',
      scheduledHrs: '7.0 hrs',
      openHrs: '1.0 hrs',
      efficiency: '88%',
      completedAppts: 8,
      noShows: 1,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      role: 'Dentist',
      totalHrs: '8 hrs',
      scheduledHrs: '6.5 hrs',
      openHrs: '1.5 hrs',
      efficiency: '81%',
      completedAppts: 7,
      noShows: 0,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      role: 'Hygienist',
      totalHrs: '8 hrs',
      scheduledHrs: '7.5 hrs',
      openHrs: '0.5 hrs',
      efficiency: '94%',
      completedAppts: 9,
      noShows: 0,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      role: 'Hygienist',
      totalHrs: '8 hrs',
      scheduledHrs: '5.5 hrs',
      openHrs: '2.5 hrs',
      efficiency: '69%',
      completedAppts: 6,
      noShows: 1,
    },
  ],
  '1W': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      role: 'Dentist',
      totalHrs: '40 hrs',
      scheduledHrs: '37 hrs',
      openHrs: '3 hrs',
      efficiency: '93%',
      completedAppts: 44,
      noShows: 3,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      role: 'Dentist',
      totalHrs: '40 hrs',
      scheduledHrs: '35 hrs',
      openHrs: '5 hrs',
      efficiency: '88%',
      completedAppts: 40,
      noShows: 4,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      role: 'Dentist',
      totalHrs: '40 hrs',
      scheduledHrs: '32 hrs',
      openHrs: '8 hrs',
      efficiency: '80%',
      completedAppts: 36,
      noShows: 2,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      role: 'Hygienist',
      totalHrs: '40 hrs',
      scheduledHrs: '38 hrs',
      openHrs: '2 hrs',
      efficiency: '95%',
      completedAppts: 46,
      noShows: 1,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      role: 'Hygienist',
      totalHrs: '40 hrs',
      scheduledHrs: '30 hrs',
      openHrs: '10 hrs',
      efficiency: '75%',
      completedAppts: 34,
      noShows: 4,
    },
  ],
  '1M': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      role: 'Dentist',
      totalHrs: '168 hrs',
      scheduledHrs: '152 hrs',
      openHrs: '16 hrs',
      efficiency: '91%',
      completedAppts: 182,
      noShows: 10,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      role: 'Dentist',
      totalHrs: '168 hrs',
      scheduledHrs: '144 hrs',
      openHrs: '24 hrs',
      efficiency: '86%',
      completedAppts: 164,
      noShows: 14,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      role: 'Dentist',
      totalHrs: '168 hrs',
      scheduledHrs: '136 hrs',
      openHrs: '32 hrs',
      efficiency: '81%',
      completedAppts: 148,
      noShows: 8,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      role: 'Hygienist',
      totalHrs: '168 hrs',
      scheduledHrs: '158 hrs',
      openHrs: '10 hrs',
      efficiency: '94%',
      completedAppts: 196,
      noShows: 4,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      role: 'Hygienist',
      totalHrs: '168 hrs',
      scheduledHrs: '124 hrs',
      openHrs: '44 hrs',
      efficiency: '74%',
      completedAppts: 140,
      noShows: 18,
    },
  ],
  '1Y': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      role: 'Dentist',
      totalHrs: '2,016 hrs',
      scheduledHrs: '1,836 hrs',
      openHrs: '180 hrs',
      efficiency: '91%',
      completedAppts: 2184,
      noShows: 120,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      role: 'Dentist',
      totalHrs: '2,016 hrs',
      scheduledHrs: '1,728 hrs',
      openHrs: '288 hrs',
      efficiency: '86%',
      completedAppts: 1968,
      noShows: 168,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      role: 'Dentist',
      totalHrs: '2,016 hrs',
      scheduledHrs: '1,632 hrs',
      openHrs: '384 hrs',
      efficiency: '81%',
      completedAppts: 1776,
      noShows: 96,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      role: 'Hygienist',
      totalHrs: '2,016 hrs',
      scheduledHrs: '1,896 hrs',
      openHrs: '120 hrs',
      efficiency: '94%',
      completedAppts: 2352,
      noShows: 48,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      role: 'Hygienist',
      totalHrs: '2,016 hrs',
      scheduledHrs: '1,476 hrs',
      openHrs: '540 hrs',
      efficiency: '73%',
      completedAppts: 1680,
      noShows: 216,
    },
  ],
  YTD: [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      role: 'Dentist',
      totalHrs: '504 hrs',
      scheduledHrs: '459 hrs',
      openHrs: '45 hrs',
      efficiency: '91%',
      completedAppts: 546,
      noShows: 30,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      role: 'Dentist',
      totalHrs: '504 hrs',
      scheduledHrs: '432 hrs',
      openHrs: '72 hrs',
      efficiency: '86%',
      completedAppts: 492,
      noShows: 42,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      role: 'Dentist',
      totalHrs: '504 hrs',
      scheduledHrs: '408 hrs',
      openHrs: '96 hrs',
      efficiency: '81%',
      completedAppts: 444,
      noShows: 24,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      role: 'Hygienist',
      totalHrs: '504 hrs',
      scheduledHrs: '474 hrs',
      openHrs: '30 hrs',
      efficiency: '94%',
      completedAppts: 588,
      noShows: 12,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      role: 'Hygienist',
      totalHrs: '504 hrs',
      scheduledHrs: '369 hrs',
      openHrs: '135 hrs',
      efficiency: '73%',
      completedAppts: 420,
      noShows: 54,
    },
  ],
}

export function getChairTimeByProviderRows(
  range: RangeKey,
): ChairTimeByProviderRow[] {
  return BY_PROVIDER[range]
}

/* ─── Chair time by time period ───────────────────────────────────────────── */

export type ChairTimeByPeriodRow = {
  id: string
  timePeriod: string
  totalHrs: string
  scheduledHrs: string
  openHrs: string
  efficiency: string
  completedAppts: number
  cancellations: number
  noShows: number
}

const BY_PERIOD: Record<RangeKey, ChairTimeByPeriodRow[]> = {
  '1D': [
    {
      id: '1',
      timePeriod: 'Today',
      totalHrs: '40 hrs',
      scheduledHrs: '34 hrs',
      openHrs: '6 hrs',
      efficiency: '89%',
      completedAppts: 33,
      cancellations: 2,
      noShows: 3,
    },
    {
      id: '2',
      timePeriod: 'Yesterday',
      totalHrs: '40 hrs',
      scheduledHrs: '33 hrs',
      openHrs: '7 hrs',
      efficiency: '85%',
      completedAppts: 30,
      cancellations: 3,
      noShows: 4,
    },
    {
      id: '3',
      timePeriod: 'Last week same day',
      totalHrs: '40 hrs',
      scheduledHrs: '32 hrs',
      openHrs: '8 hrs',
      efficiency: '82%',
      completedAppts: 29,
      cancellations: 4,
      noShows: 3,
    },
  ],
  '1W': [
    {
      id: '1',
      timePeriod: 'This week',
      totalHrs: '200 hrs',
      scheduledHrs: '172 hrs',
      openHrs: '28 hrs',
      efficiency: '86%',
      completedAppts: 164,
      cancellations: 8,
      noShows: 12,
    },
    {
      id: '2',
      timePeriod: 'Last week',
      totalHrs: '200 hrs',
      scheduledHrs: '164 hrs',
      openHrs: '36 hrs',
      efficiency: '82%',
      completedAppts: 155,
      cancellations: 10,
      noShows: 15,
    },
    {
      id: '3',
      timePeriod: 'Last month same week',
      totalHrs: '200 hrs',
      scheduledHrs: '160 hrs',
      openHrs: '40 hrs',
      efficiency: '80%',
      completedAppts: 150,
      cancellations: 12,
      noShows: 14,
    },
  ],
  '1M': [
    {
      id: '1',
      timePeriod: 'This month',
      totalHrs: '840 hrs',
      scheduledHrs: '714 hrs',
      openHrs: '126 hrs',
      efficiency: '85%',
      completedAppts: 680,
      cancellations: 34,
      noShows: 54,
    },
    {
      id: '2',
      timePeriod: 'Last month',
      totalHrs: '840 hrs',
      scheduledHrs: '696 hrs',
      openHrs: '144 hrs',
      efficiency: '83%',
      completedAppts: 660,
      cancellations: 42,
      noShows: 60,
    },
    {
      id: '3',
      timePeriod: 'Last quarter',
      totalHrs: '2,520 hrs',
      scheduledHrs: '2,100 hrs',
      openHrs: '420 hrs',
      efficiency: '83%',
      completedAppts: 2010,
      cancellations: 110,
      noShows: 165,
    },
  ],
  '1Y': [
    {
      id: '1',
      timePeriod: 'This year',
      totalHrs: '10,080 hrs',
      scheduledHrs: '8,568 hrs',
      openHrs: '1,512 hrs',
      efficiency: '85%',
      completedAppts: 8160,
      cancellations: 408,
      noShows: 648,
    },
    {
      id: '2',
      timePeriod: 'Last year',
      totalHrs: '10,080 hrs',
      scheduledHrs: '8,232 hrs',
      openHrs: '1,848 hrs',
      efficiency: '82%',
      completedAppts: 7800,
      cancellations: 480,
      noShows: 720,
    },
    {
      id: '3',
      timePeriod: '2 years ago',
      totalHrs: '10,080 hrs',
      scheduledHrs: '7,862 hrs',
      openHrs: '2,218 hrs',
      efficiency: '78%',
      completedAppts: 7440,
      cancellations: 540,
      noShows: 796,
    },
  ],
  YTD: [
    {
      id: '1',
      timePeriod: 'Jan 2026',
      totalHrs: '840 hrs',
      scheduledHrs: '714 hrs',
      openHrs: '126 hrs',
      efficiency: '85%',
      completedAppts: 680,
      cancellations: 34,
      noShows: 52,
    },
    {
      id: '2',
      timePeriod: 'Feb 2026',
      totalHrs: '840 hrs',
      scheduledHrs: '722 hrs',
      openHrs: '118 hrs',
      efficiency: '86%',
      completedAppts: 688,
      cancellations: 30,
      noShows: 48,
    },
    {
      id: '3',
      timePeriod: 'Mar 2026',
      totalHrs: '840 hrs',
      scheduledHrs: '706 hrs',
      openHrs: '134 hrs',
      efficiency: '84%',
      completedAppts: 672,
      cancellations: 36,
      noShows: 56,
    },
  ],
}

export function getChairTimeByPeriodRows(
  range: RangeKey,
): ChairTimeByPeriodRow[] {
  return BY_PERIOD[range]
}
